import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { z } from "zod";
import { FocusRun, FOCUS_PRESETS, Achievements, type Account } from "@ca/shared";
import { CardHead, Chip, Fish, Kpi, Logo, PageHead, Ring, Seg, Switch } from "@ca/ui";
import { api, HttpError, loadMe } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { useToday } from "./productivity.tsx";
import { plusDay, targetFor, useCalendar } from "./planner.tsx";
import { useActivity } from "./day-end.tsx";
import { NewBadge } from "./flags.tsx";
import { featureOn } from "./features.ts";
import { HeatLegend, fmtMin, heatClass } from "./charts.tsx";

type Preset = keyof typeof FOCUS_PRESETS;
type Background = "ocean" | "forest" | "night";
interface Task {
  path: string;
  label: string;
  icon: string;
}
const presetSecs = (p: string) =>
  p in FOCUS_PRESETS ? FOCUS_PRESETS[p as Preset][0] : FOCUS_PRESETS["25/5"][0];
/** Số hiệp gợi ý mỗi ngày (chấm tiến độ ở màn tập trung). */
const PRESET_NOTE: Record<Preset, string> = {
  "25/5": "hiệp chuẩn",
  "50/10": "đề dài, Part 7",
  "15/3": "ôn nhanh khi mệt",
};
const SOUNDS: [string, string][] = [
  ["none", "Im lặng"],
  ["rain", "🌧 Mưa"],
  ["wave", "🌊 Sóng biển"],
];
const SPEECH_FOCUS = [
  "Đọc trọn câu chứa từ khoá rồi mới chọn nhé.",
  "Đáp án phải có dẫn chứng trong bài, đừng suy diễn quá xa.",
  "Úp điện thoại xuống chưa? Cá bơi cùng bạn đây 🐠",
  "Câu khó thì đánh dấu, làm câu sau trước.",
  "Sắp được nghỉ rồi, cố thêm chút nữa!",
];
const SPEECH_BREAK = [
  "Zzz… đứng dậy vươn vai đi, Cá ngủ chút.",
  "Uống một ngụm nước nhé 💧",
  "Nhìn ra xa 20 giây cho mắt nghỉ.",
];
/* Lựa chọn của riêng máy này (nhịp, âm nền, hình nền, việc đang học): chỉ là tiện lợi, mất cũng không sao. */
function pref(key: string) {
  try {
    return localStorage.getItem(`ca-focus-${key}`);
  } catch {
    return null;
  }
}
function setPref(key: string, value: string) {
  try {
    localStorage.setItem(`ca-focus-${key}`, value);
  } catch {
    /* trình duyệt chặn lưu trữ */
  }
}
const readPreset = (): Preset => {
  const v = pref("preset");
  return v && v in FOCUS_PRESETS ? (v as Preset) : "25/5";
};
const savePreset = (v: Preset) => {
  setPref("preset", v);
};
const readSound = () => {
  const v = pref("sound");
  return SOUNDS.some((s) => s[0] === v) ? (v ?? "none") : "none";
};
const saveSound = (v: string) => {
  setPref("sound", v);
  window.dispatchEvent(new Event("ca-focus-sound"));
};
const readBackground = (): Background => {
  const v = pref("background");
  return v === "forest" || v === "night" ? v : "ocean";
};
const saveBackground = (v: Background) => {
  setPref("background", v);
};
const readTask = (): Task => {
  try {
    const v = JSON.parse(pref("task") ?? "null") as Task | null;
    if (v?.path && v.label) return v;
  } catch {
    /* bỏ qua */
  }
  return { path: "/study", label: "Chọn bài ở Làm bài", icon: "📖" };
};
const saveTask = (t: Task) => {
  setPref("task", JSON.stringify(t));
};
const FocusState = z.object({
  run: FocusRun.nullable(),
  rounds: z.array(
    z.object({
      id: z.string(),
      preset: z.string(),
      completedAt: z.string(),
      /** Gồm cả phút cộng thêm bằng "+5 phút"; server cũ chưa trả thì tính theo preset. */
      focusSecs: z.number().optional(),
    }),
  ),
  serverNow: z.string(),
});
/** Mục tiêu hiệp tập trung mỗi ngày trong Cài đặt; đọc chung cache `["me"]` của khung app. */
export function useDailyRounds() {
  return (
    useQuery({
      queryKey: ["me"],
      queryFn: loadMe,
      staleTime: Infinity,
      select: (me: Account) => me.profile.settings.focusRounds,
    }).data ?? 4
  );
}
export function useFocus(uid: string) {
  return useQuery({
    queryKey: ["focus", uid],
    queryFn: async () => FocusState.parse(await api("/v1/me/focus", { accountId: uid })),
    refetchInterval: (q) => (q.state.data?.run ? 5000 : 30000),
  });
}
/** Tạm dừng / tiếp tục / +5 phút / bỏ qua hiệp; server giữ trạng thái nên gọi lặp an toàn. */
export async function focusAction(
  uid: string,
  id: string,
  action: "pause" | "resume" | "extend" | "skip",
) {
  await api(`/v1/me/focus/${id}/${action}`, { accountId: uid, method: "POST" });
  window.dispatchEvent(new Event("ca-focus-refresh"));
}
/** Nghỉ 5 phút ngay để mở game; đang tập trung thì hiệp đó dừng, không tính hiệp. */
export async function startBreak(uid: string) {
  await api(`/v1/me/breaks/${uuidv7()}`, { accountId: uid, method: "PUT" });
  window.dispatchEvent(new Event("ca-focus-refresh"));
}
/** Giây còn lại của phần đang chạy; lúc tạm dừng thì đứng yên ở mốc bấm dừng. */
export function focusLeft(run: FocusRun | null | undefined, now: number) {
  if (!run) return 0;
  const resting = !!run.completedAt && now < Date.parse(run.breakEndsAt ?? "");
  const end = Date.parse((resting ? run.breakEndsAt : run.focusEndsAt) ?? "");
  const at = !resting && run.pausedAt ? Date.parse(run.pausedAt) : now;
  return Math.max(0, Math.ceil((end - at) / 1000));
}
export function useFocusClock(uid: string) {
  const q = useFocus(uid),
    anchor = useRef({ server: 0, perf: 0 });
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (q.data) {
      anchor.current = {
        server: Date.parse(q.data.serverNow) + Math.max(0, Date.now() - q.dataUpdatedAt),
        perf: performance.now(),
      };
      setNow(anchor.current.server);
    }
  }, [q.data, q.dataUpdatedAt]);
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(anchor.current.server + performance.now() - anchor.current.perf);
    }, 250);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return { ...q, now };
}
export async function beginFocus(
  uid: string,
  preset: "25/5" | "50/10" | "15/3" = readPreset(),
  background: "ocean" | "forest" | "night" = readBackground(),
) {
  const stored = sessionStorage.getItem(`focus-start:${uid}`),
    id = stored ?? uuidv7();
  sessionStorage.setItem(`focus-start:${uid}`, id);
  let run: unknown;
  try {
    run = await api(`/v1/me/focus/${id}`, {
      accountId: uid,
      method: "PUT",
      body: { preset, background },
    });
  } catch (e) {
    if (e instanceof HttpError) sessionStorage.removeItem(`focus-start:${uid}`);
    throw e;
  }
  sessionStorage.removeItem(`focus-start:${uid}`);
  window.dispatchEvent(new Event("ca-focus-refresh"));
  return run;
}
function Ambient({ sound }: { sound: string }) {
  useEffect(() => {
    if (sound === "none") return;
    const ctx = new AudioContext(),
      size = ctx.sampleRate * 4,
      buffer = ctx.createBuffer(1, size, ctx.sampleRate),
      values = buffer.getChannelData(0);
    let brown = 0;
    for (let i = 0; i < size; i++) {
      brown = (brown + Math.random() * 0.04 - 0.02) / 1.02;
      values[i] = sound === "rain" ? (Math.random() * 2 - 1) * 0.15 : brown * 3;
    }
    const noise = ctx.createBufferSource(),
      gain = ctx.createGain(),
      filter = ctx.createBiquadFilter();
    noise.buffer = buffer;
    noise.loop = true;
    filter.type = "lowpass";
    filter.frequency.value = sound === "rain" ? 3000 : 500;
    gain.gain.value = 0.12;
    noise.connect(filter).connect(gain).connect(ctx.destination);
    noise.start();
    void ctx.resume();
    return () => {
      noise.stop();
      void ctx.close();
    };
  }, [sound]);
  return null;
}
export function FocusRuntime({ uid, notify }: { uid: string; notify: (s: string) => void }) {
  const dailyRounds = useDailyRounds();
  const q = useFocusClock(uid),
    cache = useQueryClient(),
    [full, setFull] = useState(false),
    [sound, setSound] = useState(readSound()),
    today = useToday(uid),
    rewards = useQuery({
      queryKey: ["achievements", uid],
      queryFn: async () => Achievements.parse(await api("/v1/me/achievements", { accountId: uid })),
    }),
    [finish, setFinish] = useState(false);
  const retryAt = useRef(0),
    group = useRef(false),
    attempt = useRef(false),
    run = q.data?.run;
  useEffect(() => {
    const refresh = () => {
      void cache.invalidateQueries({ queryKey: ["focus", uid] });
    };
    const auto = () => {
      void beginFocus(uid)
        .then(refresh)
        .catch((e: unknown) => {
          notify(String(e));
        });
    };
    const g = (e: Event) => {
      const d = (e as CustomEvent<{ pending: boolean; attempt: boolean }>).detail;
      group.current = d.pending;
      attempt.current = d.attempt;
    };
    const f = () => {
      setFull(true);
    };
    window.addEventListener("ca-focus-refresh", refresh);
    window.addEventListener("ca-focus-start", auto);
    window.addEventListener("ca-focus-group", g);
    window.addEventListener("ca-focus-open", f);
    return () => {
      window.removeEventListener("ca-focus-refresh", refresh);
      window.removeEventListener("ca-focus-start", auto);
      window.removeEventListener("ca-focus-group", g);
      window.removeEventListener("ca-focus-open", f);
    };
  }, [uid, cache, notify]);
  useEffect(() => {
    const paused = full || (!!run?.completedAt && q.now < Date.parse(run.breakEndsAt ?? ""));
    document.documentElement.dataset.focusPaused = String(paused);
    window.dispatchEvent(new CustomEvent("ca-focus-fullscreen", { detail: paused }));
    return () => {
      delete document.documentElement.dataset.focusPaused;
      window.dispatchEvent(new CustomEvent("ca-focus-fullscreen", { detail: false }));
    };
  }, [full, run?.completedAt, run?.breakEndsAt, q.now >= Date.parse(run?.breakEndsAt ?? "")]);
  useEffect(() => {
    if (
      !navigator.onLine ||
      !run ||
      run.completedAt ||
      run.cancelledAt ||
      run.pausedAt ||
      finish ||
      q.now < Date.parse(run.focusEndsAt) ||
      performance.now() < retryAt.current
    )
      return;
    if (attempt.current && group.current && q.now < Date.parse(run.focusEndsAt) + 180000) return;
    retryAt.current = performance.now() + 5000;
    setFinish(true);
    void api(`/v1/me/focus/${run.id}/complete`, { accountId: uid, method: "POST" })
      .then(() => {
        notify("Hoàn thành hiệp · +10 bong bóng. Nghỉ một chút nhé!");
        window.dispatchEvent(new CustomEvent("ca-focus-break", { detail: true }));
        return cache.invalidateQueries({ queryKey: ["focus", uid] });
      })
      .catch((e: unknown) => {
        notify(String(e));
      })
      .finally(() => {
        setFinish(false);
      });
  }, [uid, run, q.now, finish, cache, notify]);
  useEffect(() => {
    if (!full || !("wakeLock" in navigator)) return;
    let handle: WakeLockSentinel | null = null,
      closed = false;
    const wake = () => {
      if (document.visibilityState === "visible")
        void navigator.wakeLock
          .request("screen")
          .then((h) => {
            if (closed) void h.release();
            else handle = h;
          })
          .catch(() => {});
    };
    wake();
    document.addEventListener("visibilitychange", wake);
    return () => {
      closed = true;
      void handle?.release();
      document.removeEventListener("visibilitychange", wake);
    };
  }, [full]);
  useEffect(() => {
    if (!full) return;
    const previous = document.activeElement;
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFull(false);
      if (e.key === "Tab") {
        const nodes = Array.from(document.querySelectorAll<HTMLElement>(".focus button,.focus a"));
        const first = nodes[0],
          last = nodes.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("keydown", escape);
      if (previous instanceof HTMLElement) previous.focus();
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    };
  }, [full]);
  const resting = !!run?.completedAt && q.now < Date.parse(run.breakEndsAt ?? ""),
    paused = !!run?.pausedAt && !run.completedAt,
    left = focusLeft(run, q.now);
  useEffect(() => {
    const update = () => {
      setSound(readSound());
    };
    window.addEventListener("ca-focus-sound", update);
    return () => {
      window.removeEventListener("ca-focus-sound", update);
    };
  }, []);
  const preset = run?.preset ?? readPreset(),
    [focusSecs, breakSecs] = FOCUS_PRESETS[preset];
  const total = run
    ? resting
      ? (Date.parse(run.breakEndsAt ?? "") - Date.parse(run.completedAt ?? "")) / 1000 || breakSecs
      : focusSecs + run.extendedSecs
    : focusSecs;
  const shown = run && (!run.completedAt || resting) ? left : focusSecs;
  const pct = run && (!run.completedAt || resting) ? 100 - (left / total) * 100 : 100;
  const roundNo = Math.min(dailyRounds, (q.data?.rounds.length ?? 0) + (resting ? 0 : 1));
  const phase = resting ? "break" : "focus";
  const task = readTask();
  const minutes = Math.floor((today.data?.activeSecs ?? 0) / 60),
    goal = today.data?.goalMinutes ?? 60;
  const speech = (resting ? SPEECH_BREAK : SPEECH_FOCUS)[
    Math.floor((q.now || 0) / 20000) % (resting ? SPEECH_BREAK.length : SPEECH_FOCUS.length)
  ];
  const close = () => {
    setFull(false);
  };
  const act = (action: "pause" | "resume" | "extend" | "skip") => {
    if (run)
      void focusAction(uid, run.id, action).catch((e: unknown) => {
        notify(String(e));
      });
  };
  const begin = () => {
    void beginFocus(uid, readPreset(), (run?.background ?? readBackground()) as Background).catch(
      (e: unknown) => {
        notify(String(e));
      },
    );
  };
  return (
    <>
      <Ambient sound={run && (!run.completedAt || resting) ? sound : "none"} />
      {(() => {
        const active = !!run && (!run.completedAt || resting);
        const span = run
          ? resting
            ? Date.parse(run.breakEndsAt ?? "") - Date.parse(run.focusEndsAt)
            : Date.parse(run.focusEndsAt) - Date.parse(run.startedAt)
          : 0;
        const done = active && span > 0 ? Math.min(100, 100 - (left * 1000 * 100) / span) : 0;
        return (
          <button
            className={`pchip${active ? (resting ? " brk" : "") : " idle"}`}
            id="pchip"
            onClick={() => {
              setFull(true);
            }}
          >
            <span>{active ? (resting ? "☕" : paused ? "⏸" : "🍅") : "⏱"}</span>
            <span id="pchipTxt">
              {active
                ? `${clock(left)}${!resting && left === 0 ? " · làm nốt cụm" : ""}`
                : "Tập trung"}
            </span>
            <span className="pbar" style={{ width: `${done}%` }} />
          </button>
        );
      })()}
      {full &&
        createPortal(
          <div
            className={`focus bg-${run?.background ?? readBackground()}`}
            id="focus"
            data-phase={phase}
            role="dialog"
            aria-modal="true"
            aria-label="Tập trung cùng Cá"
          >
            <div className="bubbles" aria-hidden="true">
              {Array.from({ length: 16 }, (_, i) => {
                const s = 8 + ((i * 7) % 26),
                  dur = 9 + ((i * 5) % 13);
                return (
                  <i
                    key={i}
                    style={{
                      left: `${(i * 61) % 100}%`,
                      width: s,
                      height: s,
                      animationDuration: `${dur}s`,
                      animationDelay: `${-((i * 3.7) % dur)}s`,
                    }}
                  />
                );
              })}
            </div>
            <header className="focus-top">
              <div className="ftl">
                <Logo size={34} />
                <span>
                  Hiệp {roundNo}/{dailyRounds}
                </span>
                <small>
                  {resting ? `· Nghỉ ${breakSecs / 60} phút` : `· Tập trung ${focusSecs / 60} phút`}
                </small>
              </div>
              <span className="spacer" />
              <button
                className="fbtn"
                aria-label="Đổi âm nền"
                onClick={() => {
                  const i = SOUNDS.findIndex((s) => s[0] === sound);
                  setSound(SOUNDS[(i + 1) % SOUNDS.length]?.[0] ?? "none");
                }}
              >
                🔈 <span className="lbl">Âm nền:</span>{" "}
                <span>{SOUNDS.find((s) => s[0] === sound)?.[1]}</span>
              </button>
              <button
                className="fbtn"
                onClick={() => {
                  void document.documentElement.requestFullscreen().catch(() => {});
                }}
              >
                ⛶ <span className="lbl">Toàn màn hình</span>
              </button>
              <button autoFocus className="fbtn" aria-label="Thu nhỏ" onClick={close}>
                ⤡ <span className="lbl">Thu nhỏ</span>
              </button>
            </header>
            <div className="focus-center">
              <div className="focus-task">
                {task.icon} {task.label}
              </div>
              <div className="focus-stage">
                <Ring pct={pct} sw={3.2} track="rgba(255,255,255,.13)" color="#B3A6F0" />
                <div className="ff" style={{ color: "#F4F2FA" }}>
                  <Fish pose={resting ? "sleep" : undefined} />
                </div>
                {run && <div className="fspeech">{speech}</div>}
              </div>
              <div className="focus-time" role="timer">
                {clock(shown)}
              </div>
              <div className="focus-phase">
                {resting
                  ? "GIỜ NGHỈ"
                  : paused
                    ? "TẠM DỪNG"
                    : run && !run.completedAt
                      ? "TẬP TRUNG"
                      : "SẴN SÀNG"}
              </div>
              <div className="focus-dots">
                {/* Mục tiêu lớn thì chỉ còn dòng "Hiệp x/N", không vẽ một hàng chấm quá dài. */}
                {Array.from({ length: dailyRounds <= 12 ? dailyRounds : 0 }, (_, i) => (
                  <i
                    key={i}
                    className={
                      i + 1 < roundNo || (i + 1 === roundNo && resting)
                        ? "on"
                        : i + 1 === roundNo
                          ? "cur"
                          : ""
                    }
                  />
                ))}
              </div>
              {run && !resting && left === 0 && (
                <div className="focus-hint">Làm nốt cụm câu trước khi nghỉ · tối đa 3 phút</div>
              )}
              <div className="focus-break">
                <span>🧍 Đứng dậy vươn vai</span>
                <span>💧 Uống nước</span>
                <span>👀 Nhìn xa 20 giây</span>
                {featureOn("game") && (
                  <Link to="/$" params={{ _splat: "game" }} className="fbtn pri" onClick={close}>
                    🎮 Chơi game giờ nghỉ
                  </Link>
                )}
                <button className="fbtn" onClick={begin}>
                  Vào hiệp mới ngay
                </button>
              </div>
              <div className="focus-ctrl">
                {!run || (run.completedAt && !resting) ? (
                  <button className="fbtn pri" onClick={begin}>
                    ▶ Bắt đầu hiệp {readPreset()}
                  </button>
                ) : !resting ? (
                  <>
                    <Link
                      to="/$"
                      params={{ _splat: task.path.replace(/^\//, "") }}
                      className="fbtn pri"
                      onClick={close}
                    >
                      📖 Mở bài
                    </Link>
                    <button
                      className="fbtn"
                      onClick={() => {
                        act(paused ? "resume" : "pause");
                      }}
                    >
                      {paused ? "▶ Tiếp tục" : "⏸ Tạm dừng"}
                    </button>
                    <button
                      className="fbtn"
                      onClick={() => {
                        act("extend");
                      }}
                    >
                      +5 phút
                    </button>
                    <button
                      className="fbtn"
                      data-tip="Bỏ qua|Sang giờ nghỉ ngay; hiệp này không tính xong và không cộng 🫧"
                      onClick={() => {
                        act("skip");
                      }}
                    >
                      ⏭ Bỏ qua
                    </button>
                    <button
                      className="fbtn"
                      onClick={() => {
                        if (!confirm("Dừng hiệp này? Hiệp chưa hoàn thành sẽ không được thưởng."))
                          return;
                        void api(`/v1/me/focus/${run.id}`, { accountId: uid, method: "DELETE" })
                          .then(() => window.dispatchEvent(new Event("ca-focus-refresh")))
                          .catch((e: unknown) => {
                            notify(String(e));
                          });
                      }}
                    >
                      ↺ Dừng hiệp
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="fbtn"
                      onClick={() => {
                        act("extend");
                      }}
                    >
                      +5 phút nghỉ
                    </button>
                    <button
                      className="fbtn"
                      onClick={() => {
                        act("skip");
                      }}
                    >
                      ⏭ Bỏ qua nghỉ
                    </button>
                  </>
                )}
              </div>
              <div className="focus-hint">
                Phím tắt: Esc thu nhỏ · bấm Toàn màn hình để che các tab khác
              </div>
            </div>
            <footer className="focus-foot">
              <span>
                Hôm nay <b>{minutes}</b>/{goal} phút
              </span>
              <span className="fprog">
                <i style={{ width: `${Math.min(100, (minutes / goal) * 100)}%` }} />
              </span>
              <span>
                🫧 <b>{rewards.data?.points ?? 0}</b> · +10 khi xong hiệp
              </span>
              <button
                className="fbtn"
                onClick={() => {
                  close();
                  window.dispatchEvent(new Event("ca-day-end"));
                }}
              >
                ■ Kết thúc buổi học
              </button>
            </footer>
          </div>,
          document.body,
        )}
    </>
  );
}
export function clock(secs: number) {
  return `${Math.floor(secs / 60)
    .toString()
    .padStart(2, "0")}:${(secs % 60).toString().padStart(2, "0")}`;
}

/** Màn Tập trung — `S.focus` của bản mẫu (spec 13). */
export function FocusPage({ uid }: { uid: string }) {
  const dailyRounds = useDailyRounds();
  const q = useFocusClock(uid),
    today = useToday(uid),
    rewards = useQuery({
      queryKey: ["achievements", uid],
      queryFn: async () => Achievements.parse(await api("/v1/me/achievements", { accountId: uid })),
    }),
    date = today.data?.date ?? "",
    month = useActivity(uid, date ? plusDay(date, -29) : "", date, !!date),
    plan = useCalendar(uid, date || "1970-01-01", date || "1970-01-01");
  const [preset, setPresetState] = useState<Preset>(readPreset()),
    [background, setBackgroundState] = useState<Background>(readBackground()),
    [sound, setSoundState] = useState(readSound()),
    [task, setTaskState] = useState(readTask());
  const run = q.data?.run;
  const resting = !!run?.completedAt && q.now < Date.parse(run.breakEndsAt ?? "");
  const active = !!run && (!run.completedAt || resting);
  const [focusSecs] = FOCUS_PRESETS[run?.preset ?? preset];
  const left = run ? focusLeft(run, q.now) : focusSecs;
  const rounds = q.data?.rounds ?? [];
  const todayMinutes = rounds.reduce((n, r) => n + (r.focusSecs ?? presetSecs(r.preset)) / 60, 0);
  // Phút tập trung từ thứ Hai tới hôm nay, lấy từ báo cáo 30 ngày đã tải cho lịch tháng.
  const monday = date ? plusDay(date, -((new Date(`${date}T00:00:00`).getDay() + 6) % 7)) : "";
  const weekMinutes =
    (month.data?.days ?? []).filter((d) => d.date >= monday).reduce((n, d) => n + d.focusSecs, 0) /
    60;
  const tasks: Task[] = [
    ...(plan.data ?? []).map((e) => ({
      path: `/${targetFor(e.skill)}`,
      label: e.title,
      icon: "📅",
    })),
    ...(today.data
      ? [
          {
            path: `/practice/${today.data.nextPart <= 4 ? "listening" : "reading"}/${today.data.nextPart}`,
            label: `Luyện Part ${today.data.nextPart}`,
            icon: "🎯",
          },
        ]
      : []),
    { path: "/errors", label: "Trị lỗi sai", icon: "📕" },
    { path: "/vocab", label: "Ôn từ vựng", icon: "📗" },
  ];
  const minutesByDay: Record<string, number> = {};
  for (const d of month.data?.days ?? []) minutesByDay[d.date] = d.activeSecs / 60;
  const from = date ? plusDay(date, -29) : "";
  const lead = from ? (new Date(`${from}T00:00:00`).getDay() + 6) % 7 : 0;
  const owned = rewards.data?.items.filter((x) => x.owned) ?? [];
  return (
    <>
      <PageHead
        space="prod"
        title={
          <>
            Tập trung <NewBadge />
          </>
        }
        desc="Đồng hồ Pomodoro có trang riêng và chế độ toàn màn hình với Cá ở giữa. Hiệp chạy song song với bài bạn đang làm; giờ học vẫn chỉ tính khi bạn ở trong bài."
      />
      <div className="grid g-7-5">
        <div className="stack">
          <section className="focus-preview">
            <div className="fp-stage">
              <Ring
                pct={active ? 100 - (left / (resting ? left || 1 : focusSecs)) * 100 : 100}
                sw={5}
                track="rgba(255,255,255,.12)"
                color="#B3A6F0"
              />
              <div className="fp-fish" style={{ color: "#F4F2FA" }}>
                <Fish pose={resting ? "sleep" : undefined} />
              </div>
            </div>
            <div className="fp-time" role="timer">
              {clock(left)}
            </div>
            <div className="fp-lbl">
              {resting ? "GIỜ NGHỈ" : "TẬP TRUNG"} · NHỊP {run?.preset ?? preset}
            </div>
            <button
              className="fbtn pri"
              style={{ marginTop: 8 }}
              onClick={() => window.dispatchEvent(new Event("ca-focus-open"))}
            >
              ⛶ {active ? "Mở lại toàn màn hình" : "Vào chế độ toàn màn hình"}
            </button>
          </section>
          <section className="card stack">
            <h3>Thiết lập</h3>
            <div className="row" role="group" aria-label="Nhịp Pomodoro">
              {(Object.keys(PRESET_NOTE) as Preset[]).map((k) => (
                <button
                  key={k}
                  className="preset"
                  aria-pressed={(run?.preset ?? preset) === k}
                  disabled={active}
                  onClick={() => {
                    savePreset(k);
                    setPresetState(k);
                  }}
                >
                  <b>{k}</b>
                  <small>{PRESET_NOTE[k]}</small>
                </button>
              ))}
            </div>
            <div className="field">
              <label>Đang học gì? (nút 📖 Mở bài ở màn tập trung mở thẳng việc này)</label>
              <div
                className="seg"
                role="group"
                aria-label="Đang học gì?"
                style={{ flexWrap: "wrap" }}
              >
                {tasks.map((t) => (
                  <button
                    key={t.path + t.label}
                    aria-pressed={task.path === t.path && task.label === t.label}
                    onClick={() => {
                      saveTask(t);
                      setTaskState(t);
                    }}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label>Âm nền · tạo ngay trong máy, chạy được khi không có mạng</label>
              <Seg
                label="Âm nền"
                options={SOUNDS.map(([value, label]) => ({ value, label }))}
                value={sound}
                onChange={(v) => {
                  saveSound(v);
                  setSoundState(v);
                }}
              />
            </div>
            {owned.length > 0 && (
              <div className="field">
                <label>Hình nền màn tập trung (đổi bằng bong bóng ở Thành tích)</label>
                <Seg
                  label="Hình nền"
                  options={[
                    { value: "ocean", label: "🌊 Biển" },
                    ...owned.map((x) => ({ value: x.key as Background, label: x.name })),
                  ]}
                  value={background}
                  onChange={(v) => {
                    saveBackground(v);
                    setBackgroundState(v);
                  }}
                />
              </div>
            )}
            <div className="row between">
              <span>Tự chuyển sang nghỉ khi hết hiệp</span>
              <Switch label="Tự chuyển sang nghỉ" checked disabled onChange={() => {}} />
            </div>
            <div className="row between">
              <span>Cho làm nốt cụm câu trước khi nghỉ (tối đa 3 phút)</span>
              <Switch label="Cho làm nốt cụm câu" checked disabled onChange={() => {}} />
            </div>
            {featureOn("game") && (
              <div className="row between">
                <span>🎮 Giờ nghỉ có nút chơi game (tự khoá khi hết giờ nghỉ)</span>
                <Link to="/$" params={{ _splat: "game" }} className="btn btn-line btn-sm">
                  Xem Game giờ nghỉ →
                </Link>
              </div>
            )}
          </section>
        </div>
        <div className="stack">
          <section className="card">
            <CardHead
              title="⏱ Tập trung"
              sub="Mỗi hiệp xong được +10 🫧; lịch tháng tô theo phút học mỗi ngày"
            >
              <Chip tone="dark">
                {rounds.length}/{dailyRounds} hiệp hôm nay
              </Chip>
            </CardHead>
            <div className="kpis" style={{ gridTemplateColumns: "repeat(3,minmax(0,1fr))" }}>
              <Kpi flip value={`${Math.round(todayMinutes)}p`} label="hôm nay" />
              <Kpi flip value={`${Math.round(weekMinutes)}p`} label="tuần này" />
              <Kpi flip value={today.data?.streak ?? 0} label="ngày liên tiếp" />
            </div>
            <div style={{ marginTop: 14 }}>
              <div className="heat">
                {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map((d) => (
                  <div key={d} className="hd">
                    {d}
                  </div>
                ))}
                {Array.from({ length: lead }, (_, i) => (
                  <div key={`l${i}`} />
                ))}
                {from &&
                  Array.from({ length: 30 }, (_, i) => {
                    const d = plusDay(from, i),
                      v = minutesByDay[d] ?? 0,
                      isToday = d === date;
                    return (
                      <div
                        key={d}
                        className={`hc ${v ? heatClass(v) : "h0"}${isToday ? " t" : ""}`}
                        tabIndex={0}
                        data-tip={`${d.slice(8, 10)}/${d.slice(5, 7)}${isToday ? " · hôm nay" : ""}|${v ? fmtMin(v) : "Chưa học"}`}
                      >
                        {Number(d.slice(8, 10))}
                      </div>
                    );
                  })}
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <HeatLegend miss={false} />
            </div>
          </section>
          <section className="card">
            <h3 style={{ marginBottom: 6 }}>Hiệp hôm nay</h3>
            {rounds.length ? (
              rounds.map((r) => {
                const end = new Date(r.completedAt),
                  mins = (r.focusSecs ?? presetSecs(r.preset)) / 60,
                  start = new Date(end.getTime() - mins * 60000),
                  hm = (d: Date) =>
                    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                return (
                  <div key={r.id} className="sess-row">
                    <span className="time">
                      {hm(start)}–{hm(end)}
                    </span>
                    <span className="muted" style={{ fontSize: 13 }}>
                      🍅 Hiệp {r.preset}
                    </span>
                    <b className="num">{mins}p</b>
                  </div>
                );
              })
            ) : (
              <p className="faint" style={{ fontSize: 13.5 }}>
                Chưa có hiệp nào hôm nay. Bấm “Vào chế độ toàn màn hình” để bắt đầu hiệp đầu tiên.
              </p>
            )}
          </section>
          {q.isError && <p role="alert">{String(q.error)}</p>}
        </div>
      </div>
    </>
  );
}
